import { UserPreferencesConfig } from 'softcat:config/user-preferences.ts';
import { SoftcatApiWidget, SoftcatApiWidgetUploadInitiation, SoftcatApiWidgetUploadStatus } from 'softcat:types/softcat-api.ts';
import { SearchParameterUtils } from 'softcat:utils/search-parameters.ts';
import { SoftcatApiUtils } from 'softcat:utils/softcat-api.ts';

const create = async (data: File): Promise<SoftcatApiWidget> => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);
    const parameters = new URLSearchParams(SearchParameterUtils.removeEmptySearchParameters({ territory: `${territory}` }));

    const form = new FormData();
    form.append('file', data);
    form.append('territory', `${territory}`);

    return SoftcatApiUtils.post<SoftcatApiWidget>(`/widgets?${parameters}`, { body: form });
};

const getUploadStatus = async ({ uploadId }: { uploadId: string }): Promise<SoftcatApiWidgetUploadStatus> => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);
    const parameters = new URLSearchParams(SearchParameterUtils.removeEmptySearchParameters({ territory: `${territory}` }));

    return SoftcatApiUtils.get<SoftcatApiWidgetUploadStatus>(`/widgets/uploads/${uploadId}?${parameters}`);
};

const initiateUpload = async ({ platformVersionTo }: { platformVersionTo?: string } = {}): Promise<SoftcatApiWidgetUploadInitiation> => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);
    const parameters = new URLSearchParams(SearchParameterUtils.removeEmptySearchParameters({ territory: `${territory}` }));

    return SoftcatApiUtils.post<SoftcatApiWidgetUploadInitiation>(`/widgets/uploads?${parameters}`, {
        body: { platformVersionTo },
    });
};

type FindAllParameters = { latest?: boolean; limit?: number; orderBy?: string; page?: number; query?: string };

const buildFindAllSearchParameters = ({ latest = false, limit = 10, orderBy = '', page = 0, query = '' }: FindAllParameters, nextToken?: string) => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);

    return new URLSearchParams(
        SearchParameterUtils.removeEmptySearchParameters({
            filter: `territory=${territory}${query ? `,id==${query}*` : ''}`,
            latest: `${latest ? 'true' : ''}`,
            limit: `${limit}`,
            nextToken: nextToken ?? '',
            offset: nextToken === undefined ? `${page}` : '',
            orderBy: `${orderBy}`,
            territory: `${territory}`,
        }),
    );
};

const findAll = async (parameters: FindAllParameters = {}): Promise<SoftcatApiWidget[]> => {
    return SoftcatApiUtils.get<SoftcatApiWidget[]>(`/widgets?${buildFindAllSearchParameters(parameters)}`);
};

const findAllWithNextToken = async ({ nextToken, ...parameters }: FindAllParameters & { nextToken?: string } = {}): Promise<{
    nextToken: string | undefined;
    widgets: SoftcatApiWidget[];
}> => {
    const { data, headers } = await SoftcatApiUtils.getWithHeaders<SoftcatApiWidget[]>(`/widgets?${buildFindAllSearchParameters(parameters, nextToken)}`);

    return { nextToken: headers.get('X-Next-Token') ?? undefined, widgets: data };
};

const findOne = async ({ id, version }: { id: string; version: string }): Promise<SoftcatApiWidget> => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);
    const parameters = new URLSearchParams(SearchParameterUtils.removeEmptySearchParameters({ territory: `${territory}` }));

    return SoftcatApiUtils.get<SoftcatApiWidget>(`/widgets/${id}/${version}?${parameters}`);
};

const remove = async ({ id, version }: { id: string; version: string }): Promise<void> => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);
    const parameters = new URLSearchParams(SearchParameterUtils.removeEmptySearchParameters({ territory: `${territory}` }));

    return SoftcatApiUtils.remove(`/widgets/${id}/${version}?${parameters}`);
};

const sign = async ({ file, id, password, version }: { file: File; id: string; password: string; version: string }): Promise<SoftcatApiWidget> => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);
    const parameters = new URLSearchParams(SearchParameterUtils.removeEmptySearchParameters({ territory: `${territory}` }));

    const form = new FormData();
    form.append('file', file);
    form.append('password', password);

    return SoftcatApiUtils.post<SoftcatApiWidget>(`/widgets/action/sign/${id}/${version}?${parameters}`, { body: form });
};

const update = async (data: Partial<SoftcatApiWidget>): Promise<SoftcatApiWidget> => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);
    const parameters = new URLSearchParams(SearchParameterUtils.removeEmptySearchParameters({ territory: `${territory}` }));

    return SoftcatApiUtils.put<SoftcatApiWidget>(`/widgets/${data.id}/${data.version}?${parameters}`, { body: data });
};

export const WidgetService = {
    create,
    findAll,
    findAllWithNextToken,
    findOne,
    getUploadStatus,
    initiateUpload,
    remove,
    sign,
    update,
};
